import DealAndPipelineService from "@/services/api/dealAndPipeline";
import DealsNPipelineSettingsService from "@/services/api/dealsNPipelineSettings";
import { handleResponse } from "@/services/axios";
import {
  createAsyncThunk,
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from "@reduxjs/toolkit";

interface LoadingState {
  opportunitiesByStatus: boolean;
  tags: boolean;
}

interface DealAndPipelineState {
  customers: any[];
  opportunityTags: any[];
  customerTags: any[];
  opportunities: any[];
  customerStatus: any[];
  visitPurpose: any[];
  products: any[];
  reasons: any[];
  leadTransaction: any[];
  opportunitySetting: any;
  loading: LoadingState;
  isFetching: boolean;
  error: string | null;
}

const initialState: DealAndPipelineState = {
  customers: [],
  opportunityTags: [],
  customerTags: [],
  opportunities: [],
  customerStatus: [],
  visitPurpose: [],
  products: [],
  reasons: [],
  leadTransaction: [],
  opportunitySetting: null,
  loading: {
    opportunitiesByStatus: false,
    tags: false,
  },
  isFetching: false,
  error: null,
};

export const getCustomers = createAsyncThunk(
  "deal/getCustomers",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await DealAndPipelineService.getCustomers(data);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch customers");
    }
  }
);

export const getTagList = createAsyncThunk(
  "deal/getTagList",
  async (tagTypeId: number, { rejectWithValue }) => {
    try {
      const res = await DealAndPipelineService.getTagList(tagTypeId);

      return {
        tagTypeId,
        data: handleResponse(res, rejectWithValue),
      };
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch tag list");
    }
  }
);

export const addOpportunity = createAsyncThunk(
  "deal/addOpportunity",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await DealAndPipelineService.addOpportunity(data);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to add opportunity");
    }
  }
);

export const updateOpportunity = createAsyncThunk(
  "deal/updateOpportunity",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await DealAndPipelineService.updateOpportunity(
        data.id,
        data.payload
      );
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to update opportunity");
    }
  }
);

export const deleteOpportunity = createAsyncThunk(
  "deal/deleteOpportunity",
  async (id: number, { rejectWithValue }) => {
    try {
      const res = await DealAndPipelineService.deleteOpportunity(id);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to delete opportunity");
    }
  }
);

export const getOpportunitiesByStatus = createAsyncThunk(
  "deal/getOpportunitiesByStatus",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await DealAndPipelineService.getOpportunitiesByStatus(data);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch opportunity status"
      );
    }
  }
);

export const getVisitData = createAsyncThunk(
  "deal/getVisitData",
  async (id: number, { rejectWithValue }) => {
    try {
      const res = await DealAndPipelineService.getVisitData(id);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch opportunities");
    }
  }
);

export const updateMultipleOpportunitiesStatus = createAsyncThunk(
  "deal/updateMultipleOpportunitiesStatus",
  async (data: any, { rejectWithValue }) => {
    try {
      const res =
        await DealAndPipelineService.updateMultipleOpportunitiesStatus(data);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.Message || "Failed to update opportunity status"
      );
    }
  }
);

export const getCustomerStatus = createAsyncThunk(
  "deal/getCustomerStatus",
  async (_, { rejectWithValue }) => {
    try {
      const res = await DealAndPipelineService.getCustomerStatus();
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch customer status");
    }
  }
);

export const getMasterProducts = createAsyncThunk(
  "deal/getMasterProducts",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await DealAndPipelineService.getMasterProducts(data);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch master product");
    }
  }
);

export const getOpportunityLostReason = createAsyncThunk(
  "deal/getOpportunityLostReason",
  async (_, { rejectWithValue }) => {
    try {
      const res = await DealAndPipelineService.getOpportunityLostReason();
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.Message || "Failed to fetch opportunity lost reason"
      );
    }
  }
);

export const createLostReason = createAsyncThunk(
  "deal/createLostReason",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await DealAndPipelineService.createLostReason(data);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to create lost reason");
    }
  }
);

export const getOpportunitySetting = createAsyncThunk(
  "deal/getOpportunitySetting",
  async (_, { rejectWithValue }) => {
    try {
      const res = await DealAndPipelineService.getOpportunitySetting();
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.Message || "Failed to fetch opportunity setting"
      );
    }
  }
);

export const getOpportunitiesByLeadId = createAsyncThunk(
  "deal/getOpportunitiesByLeadId",
  async (leadId: number, { rejectWithValue }) => {
    try {
      const res = await DealAndPipelineService.getOpportunitiesByLeadId(leadId);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.Message || "Failed to fetch customer opportunities"
      );
    }
  }
);

export const getVisitPurposes = createAsyncThunk(
  "deal/getVisitPurposes",
  async (_, { rejectWithValue }) => {
    try {
      const res = await DealAndPipelineService.getVisitPurposes();
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch visit purpose");
    }
  }
);

export const getLeadTransaction = createAsyncThunk(
  "deal/getLeadTransaction",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await DealAndPipelineService.getLeadTransaction(data);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch lead transaction");
    }
  }
);

export const addOpportunityReason = createAsyncThunk(
  "deal/addOpportunityReason",
  async (reason: string, { rejectWithValue, dispatch }) => {
    try {
      const res = await DealsNPipelineSettingsService.addOpportunityReason(reason);
      const output = handleResponse(res, rejectWithValue);
      dispatch(getOpportunityLostReason());
      return output;
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to add reason");
    }
  }
);

export const deleteOpportunityReason = createAsyncThunk(
  "deal/deleteOpportunityReason",
  async (id: number, { rejectWithValue, dispatch }) => {
    try {

      const res = await DealsNPipelineSettingsService.deleteOpportunityReason(id);
      const output = handleResponse(res, rejectWithValue);
      dispatch(getOpportunityLostReason());
      return output;
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to delete reason");
    }
  }
);

export const addTag = createAsyncThunk(
  "deal/addTag",
  async (data: { tagName: string; tagTypeId: number }, { rejectWithValue, dispatch }) => {
    try {
      const res = await DealsNPipelineSettingsService.addTag(data.tagName, data.tagTypeId);
      const output = handleResponse(res, rejectWithValue);
      dispatch(getTagList(data.tagTypeId));
      return output;
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to add tag");
    }
  }
);

export const updateTag = createAsyncThunk(
  "deal/updateTag",
  async (data: { Id: number; TagName: string; IsActive: boolean; TagTypeId: number; }, { rejectWithValue, dispatch }) => {
    try {
      const res = await DealsNPipelineSettingsService.updateTag(data.Id, data);
      const output = handleResponse(res, rejectWithValue);
      dispatch(getTagList(data?.TagTypeId));
      return output;
    } catch (err: any) {
      return rejectWithValue(err?.data?.payload?.Message || "Failed to update tag");
    }
  }
);

export const deleteTag = createAsyncThunk(
  "deal/deleteTag",
  async (data: { id: number; tagTypeId: number }, { rejectWithValue, dispatch }) => {
    try {
      const res = await DealsNPipelineSettingsService.deleteTag(data.id, data.tagTypeId);
      const output = handleResponse(res, rejectWithValue);
      dispatch(getTagList(data.tagTypeId));
      return output;
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to delete tag");
    }
  }
);

const dealAndPipelineSlice = createSlice({
  name: "deal",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getOpportunitiesByStatus.pending, (state) => {
        state.loading.opportunitiesByStatus = true;
      })
      .addCase(getOpportunitiesByStatus.fulfilled, (state, action) => {
        state.loading.opportunitiesByStatus = false;
        state.opportunities = action.payload || [];
      })
      .addCase(getOpportunitiesByStatus.rejected, (state) => {
        state.loading.opportunitiesByStatus = false;
      })
      .addCase(getCustomers.fulfilled, (state, action) => {
        state.customers = action.payload || [];
      })

      .addCase(getTagList.pending, (state) => {
        state.loading.tags = true;
        state.opportunityTags = [];
        state.customerTags = [];
      })
      .addCase(getTagList.fulfilled, (state, action) => {
        const { tagTypeId, data } = action.payload || [];
        if (tagTypeId === 1) {
          state.opportunityTags = data;
        }

        if (tagTypeId === 2) {
          state.customerTags = data;
        }

        state.loading.tags = false;
      })
      .addCase(getTagList.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading.tags = false;
      })
      .addCase(getCustomerStatus.fulfilled, (state, action) => {
        state.customerStatus = action.payload || [];
      })
      .addCase(getVisitPurposes.fulfilled, (state, action) => {
        state.visitPurpose = action.payload || [];
      })
      .addCase(getMasterProducts.fulfilled, (state, action) => {
        state.products = action.payload || [];
      })
      .addCase(getOpportunityLostReason.fulfilled, (state, action) => {
        state.reasons = action.payload || [];
      })
      .addCase(getOpportunitySetting.fulfilled, (state, action) => {
        state.opportunitySetting = action.payload || {};
      })
      .addCase(getLeadTransaction.fulfilled, (state, action) => {
        state.leadTransaction = action.payload || [];
      });

    // Generic loading & error
    builder
      .addMatcher(isPending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addMatcher(isFulfilled, (state) => {
        state.isFetching = false;
      })
      .addMatcher(isRejected, (state, action: any) => {
        state.isFetching = false;
        state.error = action.payload || "Something went wrong";
      });
  },
});

export default dealAndPipelineSlice.reducer;